import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { client } from 'cccisd-apollo';
import Loader from 'cccisd-loader';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'cccisd-react-router';
import _flatten from 'lodash/flatten';

import chartsQuery from '../../charts.graphql';
import { getSessionDataVariables } from '../../../../../../../reducers/reportFilters';
import MainChart from './MainChart';

const Component = props => {
    const [metrics, setMetrics] = useState([]);
    const [loading, setLoading] = useState(true);

    const { show } = props.settings;
    const { filters } = props;

    const dispatch = useDispatch();
    const { productHandle } = useParams();

    const { sessionDataVariables } = useSelector(state => state.app.reportFilters);
    const { productIds } = useSelector(state => state.app.reportFilters);

    const prodHandle = productHandle ? productHandle : productIds.find(prod => prod.value === filters.product).handle;

    useEffect(() => {
        (async () => {
            dispatch(getSessionDataVariables(prodHandle));

            try {
                await client
                    .query({
                        query: chartsQuery,
                        fetchPolicy: 'network-only',
                        variables: {
                            pawnId: +filters.participant,
                            productHandle: prodHandle,
                        },
                    })

                    .then(response => {
                        const learnerData = response.data.roles.learner.assignmentProgressList;

                        const valuesBySession = () => {
                            if (learnerData.length > 0) {
                                return learnerData.map(ap => {
                                    return {
                                        timepoint: +ap.deployment.timepoint,
                                        issueLabels: ap.issues.GroupSessionProgressIssues,
                                        issueScores: ap.issues.GroupSessionProgressRatings,
                                        progressLabels: ap.progress.GroupSessionProgressIssuesPositive,
                                        progressScores: ap.progress.GroupSessionProgressRatingsPositive,
                                    };
                                });
                            }
                            return null;
                        };

                        const results = valuesBySession();

                        const allIssues = results.map(result => {
                            return result.issueLabels;
                        });

                        const issues = _flatten(allIssues).filter(issue => issue !== null);

                        const uniqueIssues = [...new Set(issues)];

                        const allProgress = results.map(result => {
                            return result.progressLabels;
                        });

                        const progress = _flatten(allProgress).filter(prog => prog !== null);

                        const uniqueProgress = [...new Set(progress)];

                        const lookUpIssues = iss => {
                            const hasIssue = results.map(result => {
                                if (result.issueLabels && result.issueLabels.length > 0) {
                                    const index = result.issueLabels.indexOf(iss);

                                    if (result.issueLabels.includes(iss)) {
                                        return {
                                            id: iss,
                                            data: [
                                                {
                                                    x: result.timepoint,
                                                    y: result.issueScores[index],
                                                },
                                            ],
                                        };
                                    }
                                    return null;
                                }
                                return null;
                            });

                            return hasIssue.filter(res => res !== null);
                        };

                        const lookUpProgress = prog => {
                            const hasProgress = results.map(result => {
                                if (result.progressLabels && result.progressLabels.length > 0) {
                                    const index = result.progressLabels.indexOf(prog);

                                    if (result.progressLabels.includes(prog)) {
                                        return {
                                            id: prog,
                                            data: [
                                                {
                                                    x: result.timepoint,
                                                    y: result.progressScores[index],
                                                },
                                            ],
                                        };
                                    }
                                    return null;
                                }
                                return null;
                            });

                            return hasProgress.filter(res => res !== null);
                        };

                        // make an object containing each time an issue is mentioned, and record the values
                        const issuesAndValues = uniqueIssues.map(iss => lookUpIssues(iss));

                        const progressAndValues = uniqueProgress.map(prog => lookUpProgress(prog));

                        const chartData = (iss, len) => {
                            return [...Array(len).keys()].map(i => {
                                const label = iss[i][0].id;

                                // make an array with just the timepoint and value per issue
                                const scores = iss[i].map(thing => Array.from(thing.data));

                                return {
                                    id: renderLabel(label, len),
                                    data: _flatten(scores),
                                };
                            });
                        };

                        setMetrics([
                            {
                                type: 'issues',
                                settings: { ...props.settings.issues },
                                convertedData: chartData(issuesAndValues, issuesAndValues.length),
                            },
                            {
                                type: 'progress',
                                settings: { ...props.settings.progress },
                                convertedData: chartData(progressAndValues, progressAndValues.length),
                            },
                        ]);

                        setLoading(false);
                    });
            } catch (e) {
                console.error(e);
            }
        })();
    }, [filters, props.settings]);

    const renderLabel = (name, len) => {
        if (name.length > 20 && len >= 3) {
            return `${name.substring(0, 10)}...`;
        }
        if (name.length > 20) {
            return `${name.substring(0, 20)}`;
        }
        if (name === '') {
            return 'No issue label saved';
        }
        return name;
    };

    // console.log(metrics);
    return loading ? (
        <Loader type="inline" loading />
    ) : (
        <>
            {show && (
                <div>
                    {metrics.map(metric => {
                        return (
                            <div key={metric.type}>
                                <MainChart
                                    settings={metric.settings}
                                    data={metric.convertedData}
                                    sessionDataVariables={sessionDataVariables}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
};

Component.propTypes = {
    settings: PropTypes.object,
    isPreview: PropTypes.bool,
    filters: PropTypes.object,
    convertDataStructure: PropTypes.func,
    benchmarkCounter: PropTypes.func,
};

Component.defaultProps = {
    filters: {},
};

export default Component;
